import { createReducer } from "../../common/reduxUtil";
import {
  SET_SERVICES,
  SET_PRICING_PLANS,
  SET_LOADING,
  SET_ERROR,
  SET_FILTERS,
  SET_SEARCH,
  SET_SELECTED_PLAN,
  ADD_TO_CART,
  REMOVE_FROM_CART,
  UPDATE_CART_ITEM_QUANTITY,
  SET_CART_ITEM_SELECTED_ADDONS,
  CLEAR_CART,
  SET_SHOW_CART,
  SET_UPSELL_RECOMMENDATION,
  DISMISS_UPSELL,
  LOAD_CART_FROM_STORAGE,
  APPLY_COUPON,
  REMOVE_COUPON,
  SET_COUPON_LOADING,
  SET_COUPON_ERROR,
  SET_AVAILABLE_COUPONS,
} from "./types";
import { saveCartToStorage, clearCartStorage } from "./cartStorage";
import { mergeSelection } from "../../cart/selectionModel";
import { clampQuantity } from "./quantityRules";

const initialState = {
  services: [],
  // Pricing-plan rows; null until a fetch with includePlans has returned.
  pricingPlans: null,
  isLoading: false,
  error: null,
  filters: {
    category: null,
    status: "live", // Default to live services
    tags: [],
  },
  searchQuery: "",
  selectedPlan: null,
  cart: {
    items: [], // Array of service objects
    total: 0,
    showCart: false,
  },
  upsell: {
    show: false,
    recommendation: null, // { plan, savings, includedServices }
  },
  coupon: {
    applied: null, // { code, discountAmount, discountType, description }
    isLoading: false,
    error: null,
    availableCoupons: [],
  },
};

export default createReducer(initialState, {
  [SET_SERVICES]: (state, payload) => {
    return Object.assign({}, state, {
      services: payload.services,
    });
  },

  [SET_PRICING_PLANS]: (state, payload) => {
    return Object.assign({}, state, {
      pricingPlans: payload.pricingPlans,
    });
  },

  [SET_LOADING]: (state, payload) => {
    return Object.assign({}, state, {
      isLoading: payload.isLoading,
    });
  },

  [SET_ERROR]: (state, payload) => {
    return Object.assign({}, state, {
      error: payload.error,
    });
  },

  [SET_FILTERS]: (state, payload) => {
    return Object.assign({}, state, {
      filters: { ...state.filters, ...payload.filters },
    });
  },

  [SET_SEARCH]: (state, payload) => {
    return Object.assign({}, state, {
      searchQuery: payload.searchQuery,
    });
  },

  [SET_SELECTED_PLAN]: (state, payload) => {
    return Object.assign({}, state, {
      selectedPlan: payload.planId,
    });
  },

  [ADD_TO_CART]: (state, payload) => {
    const { service, quantity = 1 } = payload;
    const incomingSelection = payload.selectedAddOns || {};
    const existingItem = state.cart.items.find(
      (item) => item._id === service._id
    );

    // Union the item's existing add-on selection (per group) with any newly-
    // picked ones, so re-adding a service (or adding it with add-ons chosen on
    // the card) keeps them selected.
    const mergeAddOns = (item) => ({
      ...item,
      selectedAddOns: mergeSelection(item.selectedAddOns || {}, incomingSelection),
    });

    let updatedItems;
    if (existingItem) {
      // If service allows quantity and already in cart, update quantity
      if (service.allowQuantity) {
        // Capped like the server caps it (allowQuantity / restrictionQuantity).
        const newQuantity = clampQuantity(
          { ...existingItem, ...service },
          (existingItem.quantity || 1) + quantity
        );
        updatedItems = state.cart.items.map((item) =>
          item._id === service._id
            ? { ...mergeAddOns(item), quantity: newQuantity }
            : item
        );
      } else {
        // Don't duplicate, but still merge any newly-picked add-ons
        updatedItems = state.cart.items.map((item) =>
          item._id === service._id ? mergeAddOns(item) : item
        );
      }
    } else {
      // Add new item with quantity + the namespaced add-on selection seeded
      // from the pricing card (empty by default).
      updatedItems = [
        ...state.cart.items,
        {
          ...service,
          quantity: clampQuantity(service, quantity),
          selectedAddOns: incomingSelection,
        },
      ];
    }

    const newTotal = updatedItems.reduce(
      (sum, item) => sum + (item.priceInr || 0) * (item.quantity || 1),
      0
    );

    const updatedCart = {
      ...state.cart,
      items: updatedItems,
      total: newTotal,
      showCart: updatedItems.length > 0, // Auto-show cart when item added
    };

    // Save to localStorage
    saveCartToStorage(updatedCart);

    return Object.assign({}, state, {
      cart: updatedCart,
    });
  },

  [REMOVE_FROM_CART]: (state, payload) => {
    const updatedItems = state.cart.items.filter(
      (item) => item._id !== payload.serviceId
    );

    const newTotal = updatedItems.reduce(
      (sum, item) => sum + (item.priceInr || 0) * (item.quantity || 1),
      0
    );

    const updatedCart = {
      ...state.cart,
      items: updatedItems,
      total: newTotal,
      showCart: updatedItems.length > 0,
    };

    // Save to localStorage
    saveCartToStorage(updatedCart);

    return Object.assign({}, state, {
      cart: updatedCart,
    });
  },

  [UPDATE_CART_ITEM_QUANTITY]: (state, payload) => {
    const { serviceId, quantity } = payload;

    const updatedItems = state.cart.items.map((item) => {
      if (item._id === serviceId) {
        return { ...item, quantity: clampQuantity(item, quantity) };
      }
      return item;
    });

    const newTotal = updatedItems.reduce(
      (sum, item) => sum + (item.priceInr || 0) * (item.quantity || 1),
      0
    );

    const updatedCart = {
      ...state.cart,
      items: updatedItems,
      total: newTotal,
      showCart: updatedItems.length > 0,
    };

    // Save to localStorage
    saveCartToStorage(updatedCart);

    return Object.assign({}, state, {
      cart: updatedCart,
    });
  },

  [SET_CART_ITEM_SELECTED_ADDONS]: (state, payload) => {
    const { serviceId, selectedAddOns } = payload;

    const updatedItems = state.cart.items.map((item) =>
      item._id === serviceId ? { ...item, selectedAddOns } : item
    );

    const updatedCart = {
      ...state.cart,
      items: updatedItems,
    };

    // Persist so the selection survives reloads / other tabs
    saveCartToStorage(updatedCart);

    return Object.assign({}, state, {
      cart: updatedCart,
    });
  },

  [CLEAR_CART]: (state) => {
    // Clear from localStorage
    clearCartStorage();

    return Object.assign({}, state, {
      cart: {
        items: [],
        total: 0,
        showCart: false,
      },
      // Also clear coupon when cart is cleared
      coupon: {
        applied: null,
        isLoading: false,
        error: null,
        availableCoupons: [],
      },
    });
  },

  [SET_SHOW_CART]: (state, payload) => {
    return Object.assign({}, state, {
      cart: {
        ...state.cart,
        showCart: payload.show,
      },
    });
  },

  [SET_UPSELL_RECOMMENDATION]: (state, payload) => {
    return Object.assign({}, state, {
      upsell: {
        show: true,
        recommendation: payload.recommendation,
      },
    });
  },

  [DISMISS_UPSELL]: (state) => {
    return Object.assign({}, state, {
      upsell: {
        show: false,
        recommendation: null,
      },
    });
  },

  [LOAD_CART_FROM_STORAGE]: (state, payload) => {
    return Object.assign({}, state, {
      cart: {
        ...state.cart,
        ...payload.cart,
      },
    });
  },

  [APPLY_COUPON]: (state, payload) => {
    return Object.assign({}, state, {
      coupon: {
        ...state.coupon,
        applied: payload.couponData,
        isLoading: false,
        error: null,
      },
    });
  },

  [REMOVE_COUPON]: (state) => {
    return Object.assign({}, state, {
      coupon: {
        ...state.coupon,
        applied: null,
        error: null,
      },
    });
  },

  [SET_COUPON_LOADING]: (state, payload) => {
    return Object.assign({}, state, {
      coupon: {
        ...state.coupon,
        isLoading: payload.isLoading,
        error: payload.isLoading ? null : state.coupon.error,
      },
    });
  },

  [SET_COUPON_ERROR]: (state, payload) => {
    return Object.assign({}, state, {
      coupon: {
        ...state.coupon,
        isLoading: false,
        error: payload.error,
        applied: null,
      },
    });
  },

  [SET_AVAILABLE_COUPONS]: (state, payload) => {
    return Object.assign({}, state, {
      coupon: {
        ...state.coupon,
        availableCoupons: payload.coupons,
      },
    });
  },
});
